import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Grid, Chip, IconButton, Collapse, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Tooltip, MenuItem, useTheme, useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ApartmentIcon from '@mui/icons-material/Apartment';
import api from '../api';
import EntityDocumentsStrip from './EntityDocumentsStrip';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PropertyDashboard = ({ onOpenDocuments }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addPropertyDialog, setAddPropertyDialog] = useState(false);
  const [addUnitDialog, setAddUnitDialog] = useState(false);
  const [editUnitDialog, setEditUnitDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newProperty, setNewProperty] = useState({ name: '', address: '', description: '' });
  const [newUnit, setNewUnit] = useState({ name: '', status: 'available', rent: '' });
  const [editPropertyDialog, setEditPropertyDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [editPropertyForm, setEditPropertyForm] = useState({ name: '', address: '', description: '' });
  const [editingUnit, setEditingUnit] = useState(null);
  const [editUnitForm, setEditUnitForm] = useState({ name: '', status: 'available', rent: '' });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const res = await api.get('/api/properties');
      setProperties(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch properties', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async () => {
    try {
      await api.post('/api/properties', newProperty);
      setSnackbar({ open: true, message: 'Property added successfully!', severity: 'success' });
      setAddPropertyDialog(false);
      setNewProperty({ name: '', address: '', description: '' });
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add property', severity: 'error' });
    }
  };

  const handleAddUnit = async () => {
    try {
      await api.post(`/api/properties/${selectedProperty.id}/units`, newUnit);
      setSnackbar({ open: true, message: 'Unit added successfully!', severity: 'success' });
      setAddUnitDialog(false);
      setNewUnit({ name: '', status: 'available', rent: '' });
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add unit', severity: 'error' });
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      await api.delete(`/api/properties/${propertyId}`);
      setSnackbar({ open: true, message: 'Property deleted successfully!', severity: 'success' });
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete property', severity: 'error' });
    }
  };


  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setEditPropertyForm({
      name: property.name,
      address: property.address,
      description: property.description || ''
    });
    setEditPropertyDialog(true);
  };

  const handleUpdateProperty = async () => {
    try {
      await api.put(`/api/properties/${editingProperty.id}`, editPropertyForm);
      setSnackbar({ open: true, message: 'Property updated successfully!', severity: 'success' });
      setEditPropertyDialog(false);
      setEditingProperty(null);
      setEditPropertyForm({ name: '', address: '', description: '' });
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update property', severity: 'error' });
    }
  };

  const handleEditUnit = (property, unit) => {
    setSelectedProperty(property);
    setEditingUnit(unit);
    setEditUnitForm({
      name: unit.name || '',
      status: unit.status || 'available',
      rent: unit.rent || ''
    });
    setEditUnitDialog(true);
  };

  const handleUpdateUnit = async () => {
    try {
      await api.put(`/api/properties/${selectedProperty.id}/units/${editingUnit.id}`, editUnitForm);
      setSnackbar({ open: true, message: 'Unit updated successfully!', severity: 'success' });
      setEditUnitDialog(false);
      setEditingUnit(null);
      setEditUnitForm({ name: '', status: 'available', rent: '' });
      setSelectedProperty(null);
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update unit', severity: 'error' });
    }
  };

  const handleDeleteUnit = async (propertyId, unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await api.delete(`/api/properties/${propertyId}/units/${unitId}`);
      setSnackbar({ open: true, message: 'Unit deleted successfully!', severity: 'success' });
      fetchProperties();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete unit', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'primary';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getUnitCounts = (units) => {
    const counts = { available: 0, occupied: 0, maintenance: 0 };
    units?.forEach(unit => {
      counts[unit.status] = (counts[unit.status] || 0) + 1;
    });
    return counts;
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3, md: 4 } }}><Typography>Loading properties...</Typography></Box>;
  }

  return (
    <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          <HomeWorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Properties & Units
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddPropertyDialog(true)}
          onTouchStart={(e) => {
            e.preventDefault();
            setAddPropertyDialog(true);
          }}
          sx={{ minHeight: 44 }}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Add' : 'Add Property'}
        </Button>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {properties.map((property) => {
          const unitCounts = getUnitCounts(property.units);
          const totalUnits = property.units?.length || 0;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={property.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: { xs: 1.5, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      {property.name}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'row', sm: 'row' },
                      gap: { xs: 0.5, sm: 0 }
                    }}>
                      <Tooltip title="Edit Property">
                        <IconButton 
                          size={isMobile ? 'small' : 'medium'} 
                          sx={{ mr: { xs: 0.5, sm: 1 }, minWidth: 44, minHeight: 44 }} 
                          onClick={() => handleEditProperty(property)}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            handleEditProperty(property);
                          }}
                        >
                          <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Property">
                        <IconButton 
                          size={isMobile ? 'small' : 'medium'} 
                          color="error" 
                          sx={{ minWidth: 44, minHeight: 44 }}
                          onClick={() => handleDeleteProperty(property.id)}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            handleDeleteProperty(property.id);
                          }}
                        >
                          <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {property.address}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    mb: { xs: 1.5, sm: 2 },
                    flexWrap: 'wrap'
                  }}>
                    <Chip label={`${totalUnits} Units`} size="small" />
                    <Chip label={`${unitCounts.available} Available`} color="success" size="small" />
                    <Chip label={`${unitCounts.occupied} Occupied`} color="primary" size="small" />
                    <Chip label={`${unitCounts.maintenance} Maintenance`} color="warning" size="small" />
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 1 },
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <Button
                      size={isMobile ? 'small' : 'medium'}
                      startIcon={<ApartmentIcon />}
                      onClick={() => {
                        setSelectedProperty(property);
                        setAddUnitDialog(true);
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setSelectedProperty(property);
                        setAddUnitDialog(true);
                      }}
                      sx={{ minHeight: 44 }}
                      fullWidth={isMobile}
                    >
                      Add Unit
                    </Button>
                    
                    <Button
                      size={isMobile ? 'small' : 'medium'}
                      endIcon={<ExpandMoreIcon />}
                      onClick={() => setExpandedProperty(expandedProperty === property.id ? null : property.id)}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        setExpandedProperty(expandedProperty === property.id ? null : property.id);
                      }}
                      sx={{ minHeight: 44 }}
                      fullWidth={isMobile}
                    >
                      {expandedProperty === property.id ? 'Hide' : 'Show'} Units
                    </Button>
                  </Box>
                </CardContent>

                <Collapse in={expandedProperty === property.id}>
                  <CardContent sx={{ pt: 0, p: { xs: 1.5, sm: 2 } }}>
                    <List dense>
                      {property.units?.map((unit) => (
                        <ListItem key={unit.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={unit.name}
                            secondary={`Rent: $${unit.rent || 'N/A'}`}
                          />
                          <Chip
                            label={unit.status}
                            color={getStatusColor(unit.status)}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Tooltip title="Edit Unit">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              sx={{ minWidth: 44, minHeight: 44, mr: 0.5 }}
                              onClick={() => handleEditUnit(property, unit)}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                handleEditUnit(property, unit);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Unit">
                            <IconButton 
                              size="small" 
                              color="error" 
                              sx={{ minWidth: 44, minHeight: 44 }}
                              onClick={() => handleDeleteUnit(property.id, unit.id)}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                handleDeleteUnit(property.id, unit.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                      ))}
                    </List>
                    <EntityDocumentsStrip
                      propertyId={property.id}
                      entityLabel={property.name || property.address || 'Property'}
                      onOpenInHub={
                        typeof onOpenDocuments === 'function'
                          ? () => onOpenDocuments(property.id)
                          : null
                      }
                    />
                  </CardContent>
                </Collapse>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Property Dialog */}
      <Dialog 
        open={addPropertyDialog} 
        onClose={() => setAddPropertyDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add New Property
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Property Name"
            value={newProperty.name}
            onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Address"
            value={newProperty.address}
            onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Description"
            value={newProperty.description}
            onChange={(e) => setNewProperty({ ...newProperty, description: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddPropertyDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddProperty} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Property'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Unit Dialog */}
      <Dialog 
        open={addUnitDialog} 
        onClose={() => setAddUnitDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add New Unit to {selectedProperty?.name}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Unit Name/Number"
            value={newUnit.name}
            onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Monthly Rent"
            value={newUnit.rent}
            onChange={(e) => setNewUnit({ ...newUnit, rent: e.target.value })}
            fullWidth
            margin="dense"
            placeholder="e.g., 1200"
            inputProps={{
              pattern: "[0-9]*",
              inputMode: "numeric"
            }}
          />
          <TextField
            select
            label="Status"
            value={newUnit.status}
            onChange={(e) => setNewUnit({ ...newUnit, status: e.target.value })}
            fullWidth
            margin="dense"
          >
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="occupied">Occupied</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddUnitDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddUnit} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Unit'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Edit Property Dialog */}
      <Dialog 
        open={editPropertyDialog} 
        onClose={() => setEditPropertyDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Property
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Property Name"
            value={editPropertyForm.name}
            onChange={(e) => setEditPropertyForm({ ...editPropertyForm, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Address"
            value={editPropertyForm.address}
            onChange={(e) => setEditPropertyForm({ ...editPropertyForm, address: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Description"
            value={editPropertyForm.description}
            onChange={(e) => setEditPropertyForm({ ...editPropertyForm, description: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditPropertyDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateProperty} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Update' : 'Update Property'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog 
        open={editUnitDialog} 
        onClose={() => setEditUnitDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Unit in {selectedProperty?.name}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Unit Name/Number"
            value={editUnitForm.name}
            onChange={(e) => setEditUnitForm({ ...editUnitForm, name: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Monthly Rent"
            value={editUnitForm.rent}
            onChange={(e) => setEditUnitForm({ ...editUnitForm, rent: e.target.value })}
            fullWidth
            margin="dense"
            placeholder="e.g., 1200"
            inputProps={{
              pattern: "[0-9]*",
              inputMode: "numeric"
            }}
          />
          <TextField
            select
            label="Status"
            value={editUnitForm.status}
            onChange={(e) => setEditUnitForm({ ...editUnitForm, status: e.target.value })}
            fullWidth
            margin="dense"
          >
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="occupied">Occupied</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setEditUnitDialog(false);
              setEditingUnit(null);
              setEditUnitForm({ name: '', status: 'available', rent: '' });
              setSelectedProperty(null);
            }}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateUnit} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Update' : 'Update Unit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyDashboard; 